<script setup>
import { useEventBus } from '@vueuse/core';
import { f7 } from 'framework7-vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import VariantItemCard from './VariantItemCard.vue';
import { useVariantItemsHelpers } from '@/composables/commerce/useVariantItems.js';
import { useCart } from '@/composables/useCart.js';
import { useCurrency } from '@/composables/useCurrency.js';
import { PUSH_PRIMING_TRIGGER_EVENT } from '@/constants/push.js';
import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import {
  ORDER_BOX_ITEM_PLAN_LOCKED,
  ORDER_BOX_ITEM_STALE_STATE,
} from '@/services/orderBoxItem.service.js';
import { formatAvailabilityDate, hintCoversVariant } from '@/utils/productAvailability.js';
import { isTadbeesaRefusal, refusalPiecesLeft, resaleLineCap } from '@/utils/tadbeesa.js';

// =============================================================================
// PROPS & EMITS
// =============================================================================
const props = defineProps({
  variant: {
    type: Object,
    required: true,
    validator: (v) => v && v.id && v.name && v.price,
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  avatarConfig: {
    type: Object,
    default: () => ({
      serverUrl: '/filepond',
      maxSize: '5MB',
      acceptTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'],
    }),
  },
  availableMedia: {
    type: Array,
    default: () => [],
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  sheetMode: {
    type: Boolean,
    default: false,
  },
  csrfToken: {
    type: String,
    default: '',
  },
  enableReservation: {
    type: Boolean,
    default: true,
  },
  boxShipName: {
    type: String,
    default: '',
  },
  boxId: {
    type: [String, Number],
    default: null,
  },
  productId: {
    type: [String, Number],
    default: null,
  },
  /**
   * The product's normalised availability hint (from `availabilityHint()`), or
   * null. Passed in rather than read here so this editor stays a presentation
   * component with one owner of the contract.
   */
  availability: {
    type: Object,
    default: null,
  },
  /**
   * The ceiling on a SINGLE line's quantity on an ordinary size. A «تدبيسة»
   * size ignores it and caps each line by `resaleAvailableQty` below instead.
   */
  maxQtyPerLine: {
    type: Number,
    default: 999,
  },
  /**
   * Pieces of THIS «تدبيسة» size still on the shelf (the variant's own
   * `resaleAvailableQty`, BE→FE brief 2026-09-22), or null on an ordinary
   * size. Non-null switches the editor to the shelf's rules: one line per
   * size (the backend merges a re-add into her line), each line capped at
   * what it holds + what is left (utils/tadbeesa.js resaleLineCap), and her
   * live COLLECTED line's quantity stays editable. CartPage is the only host
   * that passes it.
   */
  resaleAvailableQty: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits([
  'update:modelValue',
  'item-added',
  'item-removed',
  'item-updated',
  'save',
  'save-success',
  'save-error',
  'cancel',
  'close',
]);

const { t, locale } = useI18n();
const { formatCurrency } = useCurrency();

// Push-priming value-moment bus — joining/placing items into an order-box is
// the PRIMARY D4 value moment (gap G6 + research Section 1.2). Emit fires only
// after a successful save; App.vue's pre-flight + per-user state skip the
// dialog for returning grantees and recent decliners.
const primingTriggerBus = useEventBus(PUSH_PRIMING_TRIGGER_EVENT);

const {
  addItems,
  isAddingItems,
  addItemsError,
  cancelItem,
  uncancelItem,
} = useCart();

// Items whose backend response told us they're locked in an active collection
// plan (HTTP 403 from /cancel). We disable Cancel + Edit reactively on these
// until the user navigates away — the backend doesn't yet expose an
// `isInActivePlan` field on the resource, so this is the fallback.
const planLockedItemIds = ref(new Set());

// =============================================================================
// COMPOSABLE - HELPERS ONLY (NO STATE MANAGEMENT)
// =============================================================================
const {
  calculateTotals,
  getFullDeposit,
  validateItems,
  createNewItem,
  normalizeItem,
  getItemsToSave,
  createSnapshot,
  hasChanges,
} = useVariantItemsHelpers({
  formatCurrency,
});

// =============================================================================
// LOCAL STATE
// =============================================================================
const localItems = ref([]);
const initialSnapshot = ref('');
// Saved quantity per existing line, as the sheet opened — what a «تدبيسة» line
// already holds, which its cap adds to the shelf count.
const savedQtys = ref(new Map());
// The backend's own refusal sentence (409 `tadbeesa.not_enough_pieces`,
// `detail` verbatim), shown inline under the toolbar until her next Save.
const refusalMessage = ref('');
// The pieces-left count a refusal reported (`meta.left`) — fresher than the
// prop until the host's next refetch replaces the prop.
const leftFromRefusal = ref(null);

// =============================================================================
// COMPUTED
// =============================================================================
const formattedPrice = computed(() => formatCurrency(props.variant.price));

const totals = computed(() =>
  calculateTotals(localItems.value, props.variant.price),
);

const hasUnsavedChanges = computed(() =>
  hasChanges(createSnapshot(localItems.value), initialSnapshot.value),
);

const validation = computed(() => validateItems(localItems.value));

const canSave = computed(() => {
  if (!hasUnsavedChanges.value) return false;
  return validation.value.canSave;
});

// ── «تدبيسة» size ───────────────────────────────────────────────────────────
//
// A limited count of real pieces on the club's shelf. The backend claims only
// the DIFFERENCE when a held line grows (`resizeResaleLine()`), and merges a
// second line of the same size into the first — so the rules here are per
// LINE (held + left) and one line per size.
const isTadbeesaSize = computed(() => props.resaleAvailableQty != null);
const piecesLeft = computed(
  () => Math.max(0, leftFromRefusal.value ?? props.resaleAvailableQty ?? 0),
);

const savedQtyOf = (item) => (item?.existingId ? savedQtys.value.get(item.existingId) ?? 0 : 0);

// Her live line: born COLLECTED (the piece is already at the club), still open
// to quantity edits while the carton is — the only COLLECTED row she may send.
// CartPage only ever offers cartons that still take items.
//
// The line itself says whether it is a «تدبيسة» line (`isResale`, BE→FE brief
// 2026-10-12). A COLLECTED catalogue line left on this size from before it
// became a تدبيسة size is not one: the backend skips an edit on it without a
// word, so a stepper there would save nothing. Only a payload from a backend
// older than the field says nothing (null) — then the size decides, as it did
// before the field existed.
const isResaleLiveLine = (item) => isTadbeesaSize.value
  && !!item?.existingId
  && item.isResale !== false
  && item.status === ItemStatus.COLLECTED
  && !planLockedItemIds.value.has(item.existingId);

// Each line's stepper ceiling. On a تدبيسة size: what the line holds plus what
// is left, minus whatever a sibling line is already reaching for past its own
// saved quantity — so two lines together can never ask for more than the shelf.
const lineCaps = computed(() => localItems.value.map((item, index) => {
  if (!isTadbeesaSize.value) return props.maxQtyPerLine;

  const claimedBySiblings = localItems.value.reduce((sum, other, j) => {
    if (j === index || other.status === ItemStatus.CANCELLED) return sum;
    return sum + Math.max(0, (other.qty || 0) - savedQtyOf(other));
  }, 0);

  return resaleLineCap(savedQtyOf(item), piecesLeft.value - claimedBySiblings);
}));

// Pull every line she can still change back under its cap — after a refusal
// taught us the real count, or a refetch lowered it while the sheet is open.
// Never below what a line already holds (its cap is held + left), and one line
// at a time because a sibling's cap moves as each one is lowered.
const clampToCaps = () => {
  if (!isTadbeesaSize.value) return;
  localItems.value.forEach((item, index) => {
    if (item.status === ItemStatus.CANCELLED) return;
    const cap = lineCaps.value[index];
    if ((item.qty || 0) > cap) updateItemField(index, 'qty', cap);
  });

  // An unsaved line whose size is now gone can hold nothing: drop it rather
  // than leave her a 0 she can't save and a delete button to go and find.
  const gone = localItems.value.filter((item, index) => !item.existingId && lineCaps.value[index] === 0);
  if (gone.length) localItems.value = localItems.value.filter((item) => !gone.includes(item));
};

// A new count from the host (refetch, realtime) is newer than any refusal's.
// If it says MORE is left than the refusal did, the refusal sentence is stale.
watch(() => props.resaleAvailableQty, (fresh) => {
  if (leftFromRefusal.value !== null && (fresh ?? 0) > leftFromRefusal.value) {
    refusalMessage.value = '';
  }
  leftFromRefusal.value = null;
  clampToCaps();
});

// One line per تدبيسة size: once she has a live line, «أضف طلب منفصل» would
// only be merged back into it by the backend, so it is not offered; and with
// nothing left on the shelf there is nothing to add.
const showAddButton = computed(() => {
  if (!isTadbeesaSize.value) return true;
  // Only a line the backend would merge a re-add into counts: an unsaved one or
  // her live COLLECTED one. A packed/shipped line of the size doesn't — a new
  // add opens a fresh line then, so the button must still be there.
  const hasLiveLine = localItems.value.some((item) => !item.existingId || isResaleLiveLine(item));
  return !hasLiveLine && piecesLeft.value > 0;
});

// ── Availability warning ─────────────────────────────────────────────────────
//
// Fires only when the mark actually covers THIS variant, or claims the whole
// product. A sibling variant being out is not this member's problem and must not
// warn her — the same narrowing the backend applies to an order line.
//
// It WARNS, it never blocks. Ordering during a mark stays allowed on purpose
// (scope lock V4): `canSave` above does not read this, and must not. A confirm
// dialog was considered and rejected — a soft block is still a block. What earns
// the member's attention instead is ADJACENCY (the strip sits immediately above
// the Save control, inside the gaze path of the tap) and PERSISTENCE (an inline
// strip, not a toast, so it survives scrolling the variant list).
//
// BY ID since 2026-09-07 (U-11). The mark now carries `variantIds` beside the
// names, so "is the variant she picked one of the ones that is out?" is an
// identity check rather than a string comparison — which used to fire on a
// different variant sharing a display name, and to miss one renamed since the
// mark was opened. The name goes second and is used only on a payload old enough
// to carry no ids at all.
const availabilityWarning = computed(() => {
  const covers = hintCoversVariant(
    props.availability,
    props.variant?.id,
    props.variant?.name,
  );
  if (!covers) return null;

  const date = formatAvailabilityDate(props.availability.since, locale.value);

  return props.availability.supplier
    ? t('product.availability.addToBoxWarning.withSupplier', {
      date,
      supplier: props.availability.supplier,
    })
    : t('product.availability.addToBoxWarning.noSupplier', { date });
});

// =============================================================================
// INITIALIZATION
// =============================================================================
const initializeLocalData = () => {
  refusalMessage.value = '';
  leftFromRefusal.value = null;
  const normalized = props.modelValue.map(item => normalizeItem(item));
  localItems.value = JSON.parse(JSON.stringify(normalized));
  initialSnapshot.value = createSnapshot(localItems.value);
  savedQtys.value = new Map(
    normalized
      .filter((item) => item.existingId && item.status !== ItemStatus.CANCELLED)
      .map((item) => [item.existingId, item.qty || 0]),
  );
};

// Identity-only watch. With deep:true Vue subscribes to nested reactive
// reads inside props.modelValue — if the parent's items are linked to
// Pinia-ORM state (loadVariantItems returns plain objects whose property
// reads happened against reactive ORM models), then OrderBoxRepository's
// internal fetchOne after a cancel can trigger reactivity that re-runs
// this watcher and resets localItems back to the pre-cancel snapshot,
// undoing our merge. Identity watching gives us the same init-on-open
// behavior (parent assigns a fresh array when the sheet opens) without
// the silent revert.
watch(() => props.modelValue, initializeLocalData, { immediate: true });

// =============================================================================
// METHODS - Item Management
// =============================================================================
const addItem = () => {
  const newItem = createNewItem();
  localItems.value = [...localItems.value, newItem];

  f7.toast.create({
    text: t('variant.feedback.itemAdded'),
    position: 'center',
    closeTimeout: 1500,
  }).open();

  emit('item-added', newItem);

  setTimeout(() => {
    const container = document.querySelector('.items-scroll-container');
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  }, 100);
};

/**
 * Locally flip an existing item's status. Synchronous — runs inside the
 * dialog.confirm callback so Vue's reactive context for this component
 * instance is preserved. Awaiting an API call inside the dialog callback
 * disconnects the reactive scope on this Vue 3.5 + Framework7 v9 sheet
 * composition (verified via runtime logging — splice landed in the ref
 * but watchers/render effects never re-fired after the await).
 */
const setLocalStatus = (index, newStatus) => {
  const current = localItems.value[index];
  if (!current) return;
  const newItems = [...localItems.value];
  newItems[index] = { ...current, status: newStatus };
  localItems.value = newItems;
};

/**
 * Non-async sheet auto-close. Calling emit('close') inside an `await`-bearing
 * callback breaks Vue's reactive scope on this component composition (see
 * the long story in the cancel/restore handlers), so we schedule a plain
 * setTimeout that fires the emit synchronously. Used after a successful
 * cancel / restore when the editor has no other unsaved edits.
 */
const scheduleSheetClose = () => {
  if (!props.sheetMode) return;
  setTimeout(() => {
    emit('close');
  }, 200);
};

const showPlanLockedDialog = () => {
  f7.dialog.alert(
    t('item.status.errors.planLocked'),
    t('general.error'),
  );
};

/**
 * Handle a typed error from the cancel / uncancel API. The local flip has
 * already happened by the time we land here — revert it so the UI matches
 * the server's actual state.
 */
const handleCancelError = (error, itemId, index, revertStatus) => {
  if (revertStatus !== undefined) {
    setLocalStatus(index, revertStatus);
  }
  if (error?.code === ORDER_BOX_ITEM_PLAN_LOCKED) {
    if (itemId) planLockedItemIds.value.add(itemId);
    showPlanLockedDialog();
    return;
  }
  if (error?.code === ORDER_BOX_ITEM_STALE_STATE) {
    const detail = error.detail || error.message || t('item.status.errors.cannotCancel');
    f7.dialog.alert(detail, t('general.error'));
    // Stale local state — re-init from props so we pick up the server's
    // actual status on the next render.
    initializeLocalData();
    return;
  }
  f7.dialog.alert(
    error?.message || t('item.feedback.updateFailed'),
    t('general.error'),
  );
};

const removeItem = (index) => {
  const item = localItems.value[index];
  if (!item) return;

  const isExisting = !!item.existingId;
  const title = isExisting ? t('item.status.confirmations.cancelTitle') : t('item.status.confirmations.deleteTitle');
  const message = isExisting ? t('item.status.confirmations.cancelMessage') : t('item.status.confirmations.deleteMessage');

  // SYNCHRONOUS confirm callback — same pattern that worked pre-April 2026.
  // Doing the local UI flip synchronously inside this callback keeps Vue's
  // reactive context intact. The API call is fire-and-forget; on failure
  // we revert.
  f7.dialog.confirm(message, title, () => {
    if (!isExisting) {
      const newItems = [...localItems.value];
      newItems.splice(index, 1);
      localItems.value = newItems;
      emit('item-removed', { item, type: 'deleted' });
      return;
    }

    const prevStatus = item.status;
    // Optimistic local flip — UI updates immediately.
    setLocalStatus(index, ItemStatus.CANCELLED);
    // Re-pin the initial snapshot so the cancel — which is persisted
    // server-side independently of the bulk-save flow — doesn't show up as
    // an "unsaved edit" that lights up the Save and Close button. If it
    // did, clicking Save would call addItems with an empty array (all
    // remaining items are now CANCELLED → filtered out) and useCart.addItems
    // throws "Box ID and items are required".
    initialSnapshot.value = createSnapshot(localItems.value);
    emit('item-removed', { item: localItems.value[index], type: 'cancelled' });

    // Fire the API call without awaiting. Vue's reactive context stays
    // clean inside this synchronous callback; the .then / .catch run later
    // in their own microtasks but only flip local state on error (in which
    // case we're revert-only, not the primary UI driver).
    cancelItem(0, props.boxId, item.existingId).catch((error) => {
      handleCancelError(error, item.existingId, index, prevStatus);
    });

    // Auto-close the sheet — cancellation is already persisted server-side,
    // so the top-bar Cancel / Save links are no longer meaningful here.
    scheduleSheetClose();
  });
};

const restoreItem = (index) => {
  const item = localItems.value[index];
  if (!item || !item.existingId) return;

  f7.dialog.confirm(
    t('item.status.confirmations.restoreMessage'),
    t('item.status.confirmations.restoreTitle'),
    () => {
      const prevStatus = item.status;
      setLocalStatus(index, ItemStatus.PENDING);
      // See removeItem for the rationale on re-pinning.
      initialSnapshot.value = createSnapshot(localItems.value);
      emit('item-updated', {
        index,
        field: 'status',
        value: ItemStatus.PENDING,
        item: localItems.value[index],
      });

      uncancelItem(0, props.boxId, item.existingId).catch((error) => {
        handleCancelError(error, item.existingId, index, prevStatus);
      });

      scheduleSheetClose();
    },
  );
};

const updateItemField = (index, field, value) => {
  if (!localItems.value[index]) return;

  const newItems = [...localItems.value];
  const item = newItems[index];

  if (field === 'qty') {
    const newQty = Math.max(0, parseInt(value) || 0);
    newItems[index] = { ...item, qty: newQty };

    if (item.reservationStatus === 'full') {
      newItems[index].depositAmount = getFullDeposit(newItems[index], props.variant.price);
    }
  } else if (field === 'depositAmount') {
    newItems[index] = { ...item, depositAmount: Math.max(0, parseInt(value) || 0) };
  } else {
    newItems[index] = { ...item, [field]: value };
  }

  localItems.value = newItems;
  emit('item-updated', { index, field, value, item: newItems[index] });
};

const handleReservationChange = (index, status) => {
  if (!localItems.value[index]) return;

  const newItems = [...localItems.value];
  const item = newItems[index];

  let depositAmount = 0;
  if (status === 'full') {
    depositAmount = getFullDeposit(item, props.variant.price);
  } else if (status === 'partial') {
    depositAmount = item.depositAmount || 0;
  }

  let reservedForName = item.reservedForName || '';
  if (status !== 'none' && !reservedForName) {
    reservedForName = props.boxShipName;
  } else if (status === 'none') {
    reservedForName = '';
  }

  newItems[index] = {
    ...item,
    reservationStatus: status,
    depositAmount,
    reservedForName,
  };

  localItems.value = newItems;
  emit('item-updated', { index, field: 'reservationStatus', value: status, item: newItems[index] });
};

// =============================================================================
// METHODS - Avatar Upload
// =============================================================================
const handleAvatarUploadSuccess = (index, data) => {
  updateItemField(index, 'avatarUrl', data.serverId);

  f7.toast.create({
    text: t('variant.feedback.avatarUploaded'),
    position: 'center',
    closeTimeout: 1500,
  }).open();
};

const handleAvatarUploadError = (index, error) => {
  console.error('Upload error:', error);
  f7.dialog.alert(t('variant.feedback.uploadFailed'), t('general.error'));
};

// =============================================================================
// METHODS - Save/Cancel
// =============================================================================
const handleSave = async () => {
  if (!canSave.value) {
    if (validation.value.errors.length > 0) {
      const errorMsg = validation.value.errors.map(e => e.message).join('\n');
      f7.dialog.alert(errorMsg, t('variant.validationErrors.title'));
    } else {
      f7.dialog.alert(t('variant.noChanges'), t('general.info'));
    }
    return;
  }

  if (!props.boxId || !props.productId) {
    console.error('Missing boxId or productId for save operation');
    f7.dialog.alert('Cannot save: Missing required information', 'Error');
    return;
  }
  // ADD THIS:
  if (isAddingItems.value) {
    return; // Prevent duplicate saves
  }

  refusalMessage.value = '';
  // How many lines this save RAISES. With more than one, a refusal's
  // `meta.left` is measured after the earlier lines' (rolled-back) claims, so
  // it undercounts — trust the host's refetch instead.
  let raisedLines = 0;

  try {
    // getItemsToSave now applies the authority filter itself: new rows, plus
    // existing rows the member may still change (QUANTITY_EDITABLE = PENDING).
    // That subsumes the CANCELLED exclusion this used to do a second time —
    // cancellation goes through the dedicated POST /order-box-items/{id}/cancel
    // action (see removeItem above), and CANCELLED is not QUANTITY_EDITABLE — and
    // it also stops the variant's COLLECTING / COLLECTED / SHIPPED siblings from
    // riding along in the payload with their ids.
    const itemsToSave = getItemsToSave(localItems.value, isResaleLiveLine);
    raisedLines = itemsToSave.filter((item) => (item.qty || 0) > savedQtyOf(item)).length;

    // Nothing the member is allowed to save — every row is cancelled, locked, or
    // zero-qty. useCart.addItems guards against empty input with "Box ID and
    // items are required", so treat this as a no-op success and just emit close.
    if (itemsToSave.length === 0) {
      initialSnapshot.value = createSnapshot(localItems.value);
      emit('save-success', { variantId: props.variant.id, items: [], result: null });
      if (props.sheetMode) {
        requestAnimationFrame(() => setTimeout(() => emit('close'), 100));
      }
      return;
    }

    const apiItems = itemsToSave.map((item) => ({
      variantId: parseInt(props.variant.id),
      qty: item.qty,
      notes: item.notes || null,
      avatarUrl: item.avatarUrl || null,
      depositAmount: item.depositAmount || 0,
      reservationStatus: item.reservationStatus || 'none',
      reservedForName: item.reservedForName || null,
      ...(item.existingId && { id: item.existingId }),
    }));

    const result = await addItems(
      0,
      props.boxId,
      parseInt(props.productId),
      apiItems,
    );

    // Rethrow the repository's own error — errorFromJsonApi's `code` and
    // `meta` are what tell a تدبيسة refusal apart below.
    if (addItemsError.value) {
      const failure = addItemsError.value;
      throw failure instanceof Error ? failure : new Error(failure?.message || 'Failed to save items');
    }

    // Gated (e.g. account suspended) — guard already toasted; close cleanly.
    if (result?.blocked) {
      if (props.sheetMode) emit('close');
      return;
    }

    if (result.success) {
      f7.toast.create({
        text: t('product.itemsAdded', { count: result.itemsCount }),
        position: 'center',
        closeTimeout: 2000,
        icon: '<i class="f7-icons">checkmark_circle_fill</i>',
      }).open();

      initialSnapshot.value = createSnapshot(localItems.value);
      // What each saved line now holds — a refetch landing before the sheet
      // closes must cap against the new quantity, not the one it opened with.
      itemsToSave.forEach((item) => {
        if (item.existingId) savedQtys.value.set(item.existingId, item.qty || 0);
      });

      // Fire push priming trigger — order-box value moment (gap G6).
      try { primingTriggerBus.emit({ moment: 'orderbox' }); } catch { /* noop */ }

      emit('save-success', {
        variantId: props.variant.id,
        items: itemsToSave,
        result,
      });

      emit('update:modelValue', itemsToSave);
      emit('save', itemsToSave);

      if (props.sheetMode) {
        requestAnimationFrame(() => {
          setTimeout(() => {
            emit('close');
          }, 100);
        });
      }
    }
  } catch (error) {
    console.error('Save failed:', error);

    emit('save-error', {
      variantId: props.variant.id,
      error,
    });

    // The shelf is short: no toast. Her sentence from the server stays in view
    // under the toolbar, her line drops to what is really there, and the sheet
    // stays open so one more Save goes through. Without `meta.left` the host
    // refetches the size on `save-error` and the prop watcher clamps.
    if (isTadbeesaSize.value && isTadbeesaRefusal(error)) {
      refusalMessage.value = error.message || t('product.addFailed');
      const left = raisedLines > 1 ? null : refusalPiecesLeft(error);
      if (left !== null) {
        leftFromRefusal.value = left;
        clampToCaps();
      }
      return;
    }

    f7.toast.create({
      text: error.message || t('product.addFailed'),
      position: 'center',
      closeTimeout: 3000,
      cssClass: 'color-red',
    }).open();
  }
};

const handleCancel = () => {
  if (hasUnsavedChanges.value) {
    f7.dialog.create({
      title: t('variant.unsavedChanges.title'),
      text: t('variant.unsavedChanges.message'),
      buttons: [
        { text: t('variant.unsavedChanges.keepEditing'), close: true },
        {
          text: t('variant.unsavedChanges.discard'),
          color: 'red',
          onClick: () => {
            initializeLocalData();
            emit('cancel');
            if (props.sheetMode) emit('close');
          },
        },
      ],
      verticalButtons: false,
    }).open();
  } else {
    emit('cancel');
    if (props.sheetMode) emit('close');
  }
};

/*watch(hasUnsavedChanges, (newValue) => {
  emit('unsaved-changes', newValue);
});*/
</script>

<template>
  <div class="variant-items-editor" :class="{ 'sheet-mode': sheetMode }">
    <!-- Sheet Toolbar -->
    <f7-toolbar v-if="sheetMode" class="sheet-toolbar" top>
      <f7-link @click="handleCancel">
        <f7-icon f7="xmark_circle" class="margin-right-half"></f7-icon>
        {{ t('item.status.actions.cancel') }}
      </f7-link>
      <f7-link
        :strong="hasUnsavedChanges && canSave"
        :class="{ 'disabled': !canSave || isAddingItems }"
        @click="handleSave"
      >
        <f7-icon
          :f7="isAddingItems ? 'arrow_clockwise_circle' : 'checkmark_circle_fill'"
          class="margin-right-half"
          :class="{ rotating: isAddingItems }"
          color="green"
        ></f7-icon>
        <span v-if="isAddingItems">{{ t('product.adding') }}</span>
        <span v-else>{{ t('variant.saveAndClose') }}</span>
      </f7-link>
    </f7-toolbar>

    <!-- Availability warning — ADJACENT to the Save control on purpose (it sits
         directly under the toolbar that carries it, inside the gaze path of the
         tap) and PERSISTENT (an inline strip, not a toast, so it survives
         scrolling the variant list). It never disables Save: ordering during a
         mark is allowed by design. -->
    <!-- A تدبيسة refusal takes the same slot and look, louder: it is the
         answer to the tap she just made. -->
    <div
      v-if="refusalMessage"
      class="availability-warning availability-warning--refusal"
      role="alert"
    >
      <f7-icon f7="exclamationmark_triangle" size="16"></f7-icon>
      <span>{{ refusalMessage }}</span>
    </div>
    <div v-else-if="availabilityWarning" class="availability-warning" role="status">
      <f7-icon f7="exclamationmark_circle" size="16"></f7-icon>
      <span>{{ availabilityWarning }}</span>
    </div>

    <!-- STICKY HEADER -->
    <div v-if="showHeader" class="sticky-header">
      <div class="variant-info-row">
        <div class="variant-name-price">
          <h4 class="variant-name">{{ variant.name }}</h4>
          <span class="unit-price">{{ formattedPrice }} / {{ t('variant.unit') }}</span>
        </div>
      </div>

      <!-- Live Summary -->
      <div class="live-summary">
        <div class="summary-item">
          <f7-icon f7="number_square_fill" size="20" color="primary"></f7-icon>
          <div class="summary-content">
            <span class="summary-label">{{ t('variant.summary.quantity') }}</span>
            <span class="summary-value">{{ totals.totalQuantity }}</span>
          </div>
        </div>

        <div class="summary-divider" />

        <div class="summary-item">
          <f7-icon f7="money_dollar_circle_fill" size="20" color="blue"></f7-icon>
          <div class="summary-content">
            <span class="summary-label">{{ t('variant.summary.total') }}</span>
            <span class="summary-value summary-total">{{ formatCurrency(totals.totalAmount) }}</span>
          </div>
        </div>

        <template v-if="enableReservation && totals.totalDeposit > 0">
          <div class="summary-divider" />

          <div class="summary-item">
            <f7-icon f7="checkmark_seal_fill" size="20" color="green"></f7-icon>
            <div class="summary-content">
              <span class="summary-label">{{ t('variant.summary.deposit') }}</span>
              <span class="summary-value summary-deposit">{{ formatCurrency(totals.totalDeposit) }}</span>
            </div>
          </div>

          <div class="summary-divider" />

          <div class="summary-item">
            <f7-icon f7="exclamationmark_circle_fill" size="20" color="orange"></f7-icon>
            <div class="summary-content">
              <span class="summary-label">{{ t('variant.summary.remaining') }}</span>
              <span class="summary-value summary-remaining">{{ formatCurrency(totals.remainingAmount) }}</span>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Add-vs-quantity coach: members confused the add action with the stepper.
         It names the «طلب منفصل» button, which a تدبيسة size doesn't offer. -->
    <div v-if="!isTadbeesaSize" class="editor-coach">
      <f7-icon f7="info_circle" size="16"></f7-icon>
      <span>{{ t('variant.itemEditorCoach') }}</span>
    </div>

    <!-- Empty State -->
    <f7-block v-if="localItems.length === 0" class="text-align-center empty-state">
      <f7-icon f7="cube_box" size="56" color="gray"></f7-icon>
      <p class="text-color-gray margin-top-half margin-bottom-half empty-title">
        {{ t('variant.emptyState.title') }}
      </p>
      <p class="text-color-gray empty-subtitle">{{ t('variant.emptyState.message') }}</p>
    </f7-block>

    <!-- Items List -->
    <div v-else class="items-scroll-container">
      <div class="items-grid">
        <variant-item-card
          v-for="(item, index) in localItems"
          :key="item.tempId"
          :item="item"
          :index="index"
          :avatar-config="avatarConfig"
          :available-media="availableMedia"
          :csrf-token="csrfToken"
          :enable-reservation="enableReservation"
          :box-ship-name="boxShipName"
          :full-deposit="getFullDeposit(item, variant.price)"
          :is-plan-locked="!!item.existingId && planLockedItemIds.has(item.existingId)"
          :max-qty="lineCaps[index]"
          :resale-editable="isResaleLiveLine(item)"
          :limited-stock="isTadbeesaSize"
          @update:qty="updateItemField(index, 'qty', $event)"
          @update:notes="updateItemField(index, 'notes', $event)"
          @update:avatar-url="updateItemField(index, 'avatarUrl', $event)"
          @update:deposit-amount="updateItemField(index, 'depositAmount', $event)"
          @update:reserved-for-name="updateItemField(index, 'reservedForName', $event)"
          @reservation-change="handleReservationChange(index, $event)"
          @remove="removeItem(index)"
          @restore="restoreItem(index)"
          @avatar-upload-success="handleAvatarUploadSuccess(index, $event)"
          @avatar-upload-error="handleAvatarUploadError(index, $event)"
        ></variant-item-card>
      </div>

      <div class="scroll-bottom-spacer" />
    </div>

    <!-- «تدبيسة»: final sale — said before she saves, not discovered on the
         line afterwards (where the return button is simply absent). -->
    <div v-if="isTadbeesaSize" class="tadbeesa-final-sale">
      <f7-icon f7="nosign" size="16" class="tadbeesa-final-sale__icon"></f7-icon>
      <span>{{ t('product.resale.finalSale') }}</span>
    </div>

    <!-- Inline add control. Separate from the per-item quantity stepper, and
         relabelled to "separate order" once a line exists, so members stop
         tapping it when they only meant to raise the quantity. -->
    <button
      v-if="showAddButton"
      type="button"
      class="add-separate-order"
      @click="addItem"
    >
      <f7-icon :f7="localItems.length ? 'rectangle_stack_badge_plus' : 'plus'" size="20"></f7-icon>
      <span>{{ localItems.length ? t('variant.addSeparateOrder') : t('variant.addItem') }}</span>
    </button>
  </div>
</template>


<style scoped>
/* Base Styles */
.variant-items-editor {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    background: var(--f7-page-bg-color);
    position: relative; /* For FAB positioning */
}

.variant-items-editor.sheet-mode {
    min-height: auto;
    height: 100%;
}

.sheet-toolbar {
    --f7-toolbar-height: 52px;
    position: sticky;
    top: 0;
    z-index: 200;
    background: var(--f7-bars-bg-color);
    border-bottom: 1px solid var(--f7-bars-border-color);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.sheet-toolbar :deep(.link) {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    font-weight: 500;
    padding: 8px 0;
    min-height: 44px;
}

.sheet-toolbar :deep(.link.disabled) {
    opacity: 0.5;
    pointer-events: none;
}

/* Sticky Header */
.sticky-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: var(--f7-page-bg-color);
    border-bottom: 1px solid var(--f7-bars-border-color);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.sheet-mode .sticky-header {
    position: relative;
    top: 0;
    margin-top: 0;
}

.variant-info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 4px;
}

.variant-name-price {
    flex: 1;
    min-width: 0;
}

.variant-name {
    margin: 0 0 2px 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--f7-theme-color);
    line-height: 1.3;
}

.unit-price {
    font-size: 13px;
    color: var(--f7-text-color);
    opacity: 0.7;
}

/* Live Summary */
.live-summary {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: linear-gradient(
        135deg,
        rgba(var(--f7-theme-color-rgb), 0.12),
        rgba(var(--f7-theme-color-rgb), 0.05)
    );
    border-radius: 12px;
    border: 1.5px solid rgba(var(--f7-theme-color-rgb), 0.2);
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.summary-item {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
}

.summary-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.summary-label {
    font-size: 10px;
    color: var(--f7-text-color);
    opacity: 0.7;
    font-weight: 500;
    white-space: nowrap;
}

.summary-value {
    font-size: 16px;
    font-weight: 700;
    color: var(--f7-theme-color);
    line-height: 1;
    white-space: nowrap;
}

.summary-total {
    color: #3b82f6;
}

.summary-deposit {
    color: #10b981;
}

.summary-remaining {
    color: #f97316;
}

.summary-divider {
    flex-shrink: 0;
    width: 1px;
    height: 36px;
    background: var(--f7-bars-border-color);
}

/* Empty State */
.empty-state {
    margin-top: 48px;
    margin-bottom: 24px;
}

.empty-title {
    font-size: 16px;
    font-weight: 500;
}

.empty-subtitle {
    font-size: 13px;
    margin: 0;
}

/* Items Container */
.items-scroll-container {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    min-height: 0;
}

.variant-items-editor:not(.sheet-mode) .items-scroll-container {
    max-height: calc(100vh - 280px);
}

.sheet-mode .items-scroll-container {
    /* No fixed cap: in sheet mode the editor is a flex column at the sheet's
       full height (F7's --f7-sheet-height, scoped to .variant-editor-sheet),
       so flex:1 + min-height:0 lets the list fill whatever the toolbar,
       header, coach and add-button leave. The old calc(85vh - 220px) was a
       stale constant (the sheet is not 85vh) that stranded list space. */
    max-height: none;
}

.items-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
}

.scroll-bottom-spacer {
    height: 16px;
    flex-shrink: 0;
}

/* Scrollbars */
.items-scroll-container::-webkit-scrollbar {
    width: 4px;
}

.items-scroll-container::-webkit-scrollbar-track {
    background: transparent;
}

.items-scroll-container::-webkit-scrollbar-thumb {
    background: rgba(var(--f7-theme-color-rgb), 0.3);
    border-radius: 4px;
}

.items-scroll-container::-webkit-scrollbar-thumb:hover {
    background: rgba(var(--f7-theme-color-rgb), 0.5);
}

.live-summary::-webkit-scrollbar {
    height: 4px;
}

.live-summary::-webkit-scrollbar-track {
    background: transparent;
}

.live-summary::-webkit-scrollbar-thumb {
    background: rgba(var(--f7-theme-color-rgb), 0.3);
    border-radius: 4px;
}

/* Rotation animation for loading state */
@keyframes rotate {
    to {
        transform: rotate(360deg);
    }
}

.rotating {
    animation: rotate 1s linear infinite;
}

/* Add-vs-quantity coach line */
.editor-coach {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 12px 16px 0;
    padding: 9px 11px;
    background: rgba(var(--f7-theme-color-rgb), 0.06);
    border-radius: 10px;
    font-size: 12px;
    line-height: 1.5;
    color: var(--f7-text-color);
}

.editor-coach :deep(.icon) {
    flex-shrink: 0;
    color: var(--f7-theme-color);
}

/* Availability warning. The muted out-of-stock awareness pair, NOT the danger
   pair: the Save button beside it stays enabled, and a red strip next to a live
   button reads as a refusal. Placed above the sticky header so it is the first
   thing under the toolbar carrying Save. */
.availability-warning {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 12px 16px 0;
    padding: 9px 11px;
    background: var(--ci-muted-bg);
    border-radius: 10px;
    font-size: 12px;
    line-height: 1.5;
    color: var(--ci-muted-fg);
}

.availability-warning :deep(.icon) {
    flex-shrink: 0;
}

/* Measured 8.75:1 (2026-09-23 design pass). */
.availability-warning--refusal {
    background: #fffbeb;
    color: #78350f;
}

.dark .availability-warning--refusal {
    background: color-mix(in srgb, var(--poolit-warning-500) 18%, transparent);
    color: var(--poolit-warning-100, #fef3c7);
}

/* Same quiet fact line as the item sheet's (ItemSheets.vue). */
.tadbeesa-final-sale {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 12px 16px 0;
    font-size: var(--poolit-font-size-sm);
    line-height: 1.5;
    color: #4b5563;
}

.tadbeesa-final-sale__icon {
    color: #4b5563;
    flex-shrink: 0;
}

.dark .tadbeesa-final-sale,
.dark .tadbeesa-final-sale__icon {
    color: var(--ci-muted-fg);
}

/* Inline "add separate order" button (replaces the old FAB) */
.add-separate-order {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: calc(100% - 32px);
    margin: 12px 16px 16px;
    min-height: 48px;
    padding: 12px;
    background: none;
    border: 1.5px solid var(--f7-theme-color);
    border-radius: 12px;
    color: var(--f7-theme-color);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}

.add-separate-order:active {
    opacity: 0.7;
}
</style>
